/**
 * Forecast chart — monthly history line spliced onto the SARIMA projection,
 * with a shaded confidence band (range Area) around the forecast.
 *
 * The wire shape is two lists (history actuals, forecast projections); we
 * merge them into a single Recharts series. The last actual point is also
 * stamped onto the forecast line/band so the dashed projection connects
 * cleanly to the observed history rather than floating detached.
 */

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import type { ForecastResponse } from '@/types/forecast';

function fmtMonthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' });
}

function makeAxisFormatter(unit: string): (v: number) => string {
  if (unit === 'ZAR') {
    return (v) => {
      if (Math.abs(v) >= 1_000_000) return `R ${(v / 1_000_000).toFixed(1)}M`;
      if (Math.abs(v) >= 1_000) return `R ${(v / 1_000).toFixed(0)}k`;
      return `R ${v.toFixed(0)}`;
    };
  }
  return (v) => {
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M L`;
    if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}k L`;
    return `${v.toFixed(0)} L`;
  };
}

interface ChartRow {
  label: string;
  actual: number | null;
  yhat: number | null;
  band: [number, number] | null;
}

function buildRows(data: ForecastResponse): ChartRow[] {
  const rows: ChartRow[] = data.history.map((h) => ({
    label: fmtMonthLabel(h.month),
    actual: h.actual,
    yhat: null,
    band: null,
  }));

  // Pin the forecast line + band onto the last observed month so they
  // connect to the history instead of starting a month later, detached.
  if (rows.length > 0 && data.forecast.length > 0) {
    const last = data.history[data.history.length - 1];
    rows[rows.length - 1].yhat = last.actual;
    rows[rows.length - 1].band = [last.actual, last.actual];
  }

  for (const f of data.forecast) {
    rows.push({
      label: fmtMonthLabel(f.month),
      actual: null,
      yhat: f.yhat,
      band: [f.yhat_lower, f.yhat_upper],
    });
  }
  return rows;
}

export interface ForecastChartProps {
  data: ForecastResponse | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function ForecastChart({ data, isLoading, error }: ForecastChartProps): JSX.Element {
  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-destructive">
        Failed to load forecast: {error.message}
      </div>
    );
  }
  if (isLoading || !data) {
    return <Skeleton className="h-full w-full" />;
  }

  const fmt = makeAxisFormatter(data.unit);
  const rows = buildRows(data);
  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No history available for this metric.
      </div>
    );
  }

  const ciLabel = `${Math.round((data.ci_level ?? 0.95) * 100)}% interval`;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 8 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} width={64} />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            fontSize: 12,
          }}
          formatter={(value) => {
            if (Array.isArray(value)) {
              return `${fmt(Number(value[0]))} – ${fmt(Number(value[1]))}`;
            }
            return fmt(Number(value));
          }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Area
          type="monotone"
          dataKey="band"
          name={ciLabel}
          stroke="none"
          fill="hsl(var(--primary))"
          fillOpacity={0.15}
          connectNulls={false}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="actual"
          name="Actual"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="yhat"
          name="Forecast"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={{ r: 2 }}
          connectNulls={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default ForecastChart;
